/**
 * 受講履歴データの暗号化/復号化サービス
 *
 * AES-GCM暗号化を使用してデータをバイナリ形式で保護する。
 * 鍵はAPP_SECRET_KEYとのXOR演算により難読化される。
 *
 * ファイル構造:
 * - [0-7]: マジックナンバー "PRECONV1" (8 bytes)
 * - [8-19]: IV 初期化ベクトル (12 bytes)
 * - [20-51]: 難読化された暗号化鍵 (32 bytes)
 * - [52-]: 暗号化されたデータ本体
 */

import {
  MAGIC_NUMBER,
  IV_SIZE,
  KEY_SIZE,
  HEADER_SIZE,
  APP_SECRET_KEY,
} from './constants';

export class EncryptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EncryptionError';
  }
}

export class DecryptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DecryptionError';
  }
}

export class InvalidFileFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidFileFormatError';
  }
}

/**
 * 暗号化サービス
 */
export class EncryptionService {
  private encoder = new TextEncoder();
  private decoder = new TextDecoder();
  private secretKeyBytes: Uint8Array;

  constructor() {
    this.secretKeyBytes = this.encoder.encode(APP_SECRET_KEY);
  }

  /**
   * バイト配列とAPP_SECRET_KEYをXOR演算する（難読化/復元）
   * XORは自己逆元なので、同じ操作で難読化と復元の両方に使える
   */
  private xorWithSecret(data: Uint8Array): Uint8Array {
    const result = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
      result[i] = data[i] ^ this.secretKeyBytes[i % this.secretKeyBytes.length];
    }
    return result;
  }

  /**
   * データを暗号化してバイナリ形式に変換する
   * @param data 暗号化するオブジェクト
   * @returns 暗号化されたバイナリデータ（Blob）
   */
  async encrypt(data: unknown): Promise<Blob> {
    try {
      // 1. データ本体用の「使い捨て鍵」を生成
      const contentKey = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );

      // 2. 鍵をバイナリ化し、APP_SECRET_KEYで難読化
      const rawKey = await crypto.subtle.exportKey('raw', contentKey);
      const obfuscatedKey = this.xorWithSecret(new Uint8Array(rawKey));

      // 3. IVを生成
      const iv = crypto.getRandomValues(new Uint8Array(IV_SIZE));

      // 4. データ本体を暗号化
      const jsonString = JSON.stringify(data);
      const encryptedContent = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        contentKey,
        this.encoder.encode(jsonString)
      );

      // 5. ファイルの組み立て
      const magicNumber = this.encoder.encode(MAGIC_NUMBER);

      return new Blob([magicNumber, iv, obfuscatedKey, encryptedContent], {
        type: 'application/octet-stream',
      });
    } catch (error) {
      throw new EncryptionError(
        `暗号化に失敗しました: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * バイナリデータを復号化してオブジェクトに変換する
   * @param arrayBuffer 暗号化されたバイナリデータ
   * @returns 復号化されたオブジェクト
   */
  async decrypt(arrayBuffer: ArrayBuffer): Promise<unknown> {
    // 1. ファイルサイズチェック
    if (arrayBuffer.byteLength < HEADER_SIZE) {
      throw new InvalidFileFormatError('このファイルは読み込めません');
    }

    // 2. マジックナンバーチェック
    const magic = this.decoder.decode(arrayBuffer.slice(0, 8));
    if (magic !== MAGIC_NUMBER) {
      throw new InvalidFileFormatError('このファイルは読み込めません');
    }

    // 3. 各パーツの切り出し
    const iv = new Uint8Array(arrayBuffer.slice(8, 8 + IV_SIZE));
    const obfuscatedKey = new Uint8Array(
      arrayBuffer.slice(8 + IV_SIZE, 8 + IV_SIZE + KEY_SIZE)
    );
    const encryptedContent = arrayBuffer.slice(HEADER_SIZE);

    // 4. 難読化された鍵を復元し、CryptoKeyを再構築
    const rawKey = this.xorWithSecret(obfuscatedKey);
    let contentKey: CryptoKey;
    try {
      contentKey = await crypto.subtle.importKey(
        'raw',
        rawKey,
        'AES-GCM',
        false,
        ['decrypt']
      );
    } catch {
      throw new DecryptionError(
        'データの復号に失敗しました。ファイルが改ざんされている可能性があります。'
      );
    }

    // 5. 復号
    try {
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        contentKey,
        encryptedContent
      );
      return JSON.parse(this.decoder.decode(decrypted));
    } catch {
      throw new DecryptionError(
        'データの復号に失敗しました。ファイルが改ざんされている可能性があります。'
      );
    }
  }
}
